import { Icon } from '@clientbase/clientbase-library'
import { Box, Chip, Typography } from '@mui/material'
import moment from 'moment'

import { Table } from 'components/Table'

import { formatMoney } from 'utils'

import { theme } from 'styles/theme'

import {
  BillingItem,
  RecurrenceCycle
} from '../Composition/Composition.interface'

/**
 * Converte o ciclo DA RECORRÊNCIA em que o item cai na ORDEM DA OCORRÊNCIA dele.
 *
 * `current_cycle` vem do backend como a posição na recorrência: um plano
 * trimestral dentro de uma recorrência mensal cai nos ciclos 1, 4, 7, 10, 13.
 * Já `product_total_cycles` conta COBRANÇAS (5). Exibir os dois lado a lado
 * misturava unidades e produzia rótulos impossíveis como "(13/5)" — o merchant
 * lia "13ª de 5".
 *
 * A ordem é quantos passos de `cycle_interval` se passaram desde `start_cycle`.
 * Para o ciclo 4 de um item que começa no 1 com passo 3: (4-1)/3 + 1 = 2.
 */
export const ocorrenciaDoItem = (item: BillingItem): number => {
  const inicio = Number(item.start_cycle) || 1
  const passo = Math.max(Number(item.cycle_interval) || 1, 1)
  const atual = Number(item.current_cycle) || inicio

  return Math.floor((atual - inicio) / passo) + 1
}

/**
 * O TOTAL de cobranças que o rótulo "N de M" anuncia.
 *
 * A janela do item (`total_cycles`) vence `product_total_cycles`: ela é o que o merchant
 * configurou em "Por quantos ciclos?", enquanto o total do produto é só o default de onde
 * ela partiu. Com uma janela de 3 cobranças num plano de 5, o rótulo precisa dizer "1 de 3":
 * dizer "1 de 5" prometeria duas cobranças que nunca vão existir.
 */
export const totalDeCobrancasDoItem = (item: BillingItem): number => {
  const janela = Number(item.total_cycles) || 0

  return janela > 0 ? janela : Number(item.product_total_cycles) || 0
}

type CyclesTableProps = {
  cycles: RecurrenceCycle[]
}

/**
 * A prévia dos ciclos da recorrência.
 *
 * Extraída do accordion para ser reaproveitada no modal de preview — as duas
 * superfícies precisam mostrar exatamente a mesma informação, e duplicar a
 * tabela deixaria uma delas para trás na primeira mudança.
 */
const CyclesTable = ({ cycles }: CyclesTableProps) => (
  <Box
    sx={{
      // Sobrescreve o cursor de linha clicável: aqui não há ação por linha.
      '& .MuiTableCell-root': {
        cursor: 'auto !important'
      }
    }}
  >
    <Table
      mobileCards
      header={[
        { id: 'dueDate', label: 'Vencimento' },
        { id: 'items', label: 'Item' },
        { id: 'amount', label: 'Valor' }
      ]}
      rows={cycles.map((cycle) => ({
        uuid: cycle.cycle.toString(),
        dueDate: moment(cycle.due_date).format('DD/MM/YYYY'),
        items: (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'wrap',
              gap: 1,
              py: 0.5
            }}
          >
            {cycle.billing_items.map((item, index) => (
              <Box
                key={index}
                sx={{
                  bgcolor: theme.palette.neutral[700],
                  borderRadius: '16px',
                  padding: '3px 16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                <Typography
                  sx={{
                    fontSize: '12px',
                    color: theme.palette.neutral[400]
                  }}
                >
                  {item.product_name || item.description}{' '}
                </Typography>

                {item.description ? (
                  ''
                ) : totalDeCobrancasDoItem(item) === 0 ? (
                  <Icon
                    icon="cached"
                    sx={{
                      width: '14px',
                      height: '14px',
                      color: theme.palette.neutral[400]
                    }}
                  />
                ) : (
                  <Typography
                    sx={{
                      fontSize: '12px',
                      color: theme.palette.neutral[400]
                    }}
                  >{`(${ocorrenciaDoItem(item)}/${totalDeCobrancasDoItem(
                    item
                  )})`}</Typography>
                )}
              </Box>
            ))}
          </Box>
        ),
        amount:
          cycle.amount_billed === 0 ? (
            <Box display="flex" alignItems="center" gap={1}>
              <Typography sx={{ color: 'success.main' }}>R$ 0,00</Typography>
              <Chip
                label="Bonificado"
                size="small"
                color="success"
                sx={{ fontSize: '10px', height: '20px' }}
              />
            </Box>
          ) : (
            formatMoney(cycle.amount_billed)
          )
      }))}
      select={{ useSelect: false }}
      hiddePagination
      hoverInRow
      disableFilter
    />
  </Box>
)

export default CyclesTable
